<template>
    <app-card title="User Organizations" class="mt-4">
        <template #header-right v-if="showEditButton && editable">
            <span v-if="!editing">
                <app-fab-btn iconName="icon-edit" color="white" x-small @click="startEditing"/>
            </span>
        </template>
        <div class="pa-4">
            <div v-if="!editing">
                <div style="display: flex; align-items: center">
                    <v-icon style="color: rgba(255,0,0,.5)" :size="16">mdi-alert</v-icon>
                    <span style="color: rgba(255,0,0,.5); font-weight: 800; margin-left: 5px">IMPORTANT NOTE: </span>
                </div>
                <div style="margin-bottom: 10px">
                    <strong>User: </strong>Users are required to be granted access to individual groups within the selected
                    organization. Users do not have access to configure settings or add/update other users on the organization.
                </div>
                <div style="margin-bottom: 10px">
                    <strong>Admin: </strong>Admins have access to all groups and profiles within the selected organization.
                    Admins can configure settings and add/update other users on the organization.
                </div>
                <div v-if="loading" style="display: flex; justify-content: center; padding: 20px">
                    Loading...
                </div>
                <template v-else>
                    <div v-for="organization in userOrganizations"
                         :key="organization.id"
                         style="border: 1px solid lightgrey; border-radius: 5px; margin: 5px 0; display: flex; align-items: center"
                         class="pa-2">
                        <div style="flex: 1">
                            {{ organization.name }}
                        </div>
                        <div v-if="!editable" style="padding: 4px 12px; background-color: #e3f2fd; border-radius: 4px; font-size: 0.875rem;">
                            {{ organization.organizationAccessTypeId === 2 ? 'Admin' : 'User' }}
                        </div>
                        <v-btn-toggle
                            v-else
                            :model-value="organization.organizationAccessTypeId"
                            mandatory
                            @update:model-value="(value) => updateAccountOrganization(organization, value)"
                            :color="organization.organizationAccessTypeId == 1 ? 'green' : 'blue'"
                        >
                            <v-btn :value="1" style="height: 30px">User</v-btn>
                            <v-btn :value="2" style="height: 30px">Admin</v-btn>
                        </v-btn-toggle>
                    </div>
                    <div v-if="!userOrganizations || userOrganizations.length < 1">
                        There are no organizations assigned to this user
                    </div>
                </template>
            </div>
            <div v-else>
                <app-form-multiple-select
                    v-model="editForm.organizationIds"
                    :items="availableOrganizations"
                    item-text="name"
                    item-value="id"
                    label="Organizations"
                    addButtonText="Add Organization"
                ></app-form-multiple-select>
                <div class="mt-2">
                    <app-btn @click="submitEditOrganizations" :loading="saving">Save Organizations</app-btn>
                    <app-btn outlined class="ml-2" @click="editing = false">Cancel</app-btn>
                </div>
            </div>
        </div>
    </app-card>
</template>

<script>
import {getAccount, updateAccount, updateAccountModel, updateAccountOrganization} from '@/services/userService'
import {getEnterpriseOrganizations} from '@/services/enterprise.service'
import {SET_SNACKBAR} from '@/store/mutation-types'

export default {
    name: "UserOrganizationsCard",
    props: {
        account: {
            type: Object,
            required: true
        },
        editable: {
            type: Boolean,
            default: true
        },
        showEditButton: {
            type: Boolean,
            default: true
        }
    },
    emits: ['org-role-changed', 'orgs-saved'],
    data() {
        return {
            loading: false,
            saving: false,
            editing: false,
            userOrganizations: [],
            availableOrganizations: [],
            editForm: {
                organizationIds: [],
                organizations: []
            }
        }
    },
    async mounted() {
        await this.loadOrganizations()
    },
    watch: {
        'account.id'(newId, oldId) {
            if (newId && newId !== oldId) {
                this.editing = false
                this.loadOrganizations()
            }
        }
    },
    methods: {
        async loadOrganizations() {
            this.loading = true
            const response = await getAccount(this.account.id)
            if (response?.isSuccess) {
                this.userOrganizations = response.organizations || []
            }
            this.loading = false
        },
        async loadAvailableOrganizations() {
            const response = await getEnterpriseOrganizations(true)
            if (response?.isSuccess) {
                this.availableOrganizations = response.organizations || []
            }
        },
        async startEditing() {
            await this.loadAvailableOrganizations()
            this.editForm = {
                organizationIds: this.userOrganizations.map(o => o.id),
                organizations: [...this.userOrganizations]
            }
            this.editing = true
        },
        async updateAccountOrganization(organization, organizationAccessTypeId) {
            if (organization.organizationAccessTypeId === organizationAccessTypeId) return

            const previous = organization.organizationAccessTypeId
            organization.organizationAccessTypeId = organizationAccessTypeId

            const request = {
                accountId: this.account.id,
                organizationId: organization.id,
                organizationAccessTypeId
            }
            const response = await updateAccountOrganization(request)

            if (!response?.isSuccess) {
                organization.organizationAccessTypeId = previous
                return
            }

            this.$store.commit(SET_SNACKBAR, {
                text: 'Organization role updated.',
                color: 'success',
                open: true
            })

            this.$emit('org-role-changed', { organization, organizationAccessTypeId })
        },
        async submitEditOrganizations() {
            this.saving = true
            const {
                email, firstName, lastName, phoneNumber, jobTitle,
                isSafetyCoach, professionalTypeId, selectedOrganizationAccessTypeId,
                isBillingContact, isOptimizedSyncEnabled, optimizedSyncTimePeriodDays
            } = this.account

            const accountId = this.account.id
            const accountOrganizations = (this.editForm.organizationIds || []).map(orgId => {
                const existing = this.editForm.organizations.find(o => o.id === orgId)
                return {
                    accountId,
                    organizationId: orgId,
                    organizationAccessTypeId: existing?.organizationAccessTypeId || 1
                }
            })

            const request = {...updateAccountModel}
            request.id = accountId
            request.email = email
            request.firstName = firstName
            request.lastName = lastName
            request.phoneNumber = phoneNumber
            request.jobTitle = jobTitle
            request.isSafetyCoach = isSafetyCoach
            request.professionalTypeId = professionalTypeId
            request.organizationAccessTypeId = selectedOrganizationAccessTypeId
            request.accountOrganizations = accountOrganizations
            request.updateGroups = false
            request.updateOrganizations = true
            request.isBillingContact = isBillingContact
            request.isOptimizedSyncEnabled = isOptimizedSyncEnabled
            request.optimizedSyncTimePeriodDays = optimizedSyncTimePeriodDays

            const response = await updateAccount(request)

            if (!response?.isSuccess) {
                this.saving = false
                return
            }

            await this.loadOrganizations()

            this.$store.commit(SET_SNACKBAR, {
                text: 'Organizations successfully updated.',
                color: 'success',
                open: true
            })

            this.saving = false
            this.editing = false

            this.$emit('orgs-saved', response.account)
        }
    }
}
</script>
